import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { User as UserIcon, Camera, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

// Reuses backend endpoints:
//   POST   /api/users/me/photo       (multipart file)
//   DELETE /api/users/me/photo
// Shown inside the AdminDashboard header (my account button opens this).
export default function ProfilePhotoWidget({ user, onChange, compact = false, editable = false }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const src = user?.picture
    ? (user.picture.startsWith("/api/") ? backendUrl + user.picture : user.picture)
    : null;

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("Image is larger than 8 MB"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/users/me/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange?.(data);
      toast.success("Profile photo updated");
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("Remove your profile photo?")) return;
    setBusy(true);
    try {
      const { data } = await api.delete("/users/me/photo");
      onChange?.(data);
      toast.success("Profile photo removed");
      setOpen(false);
    } catch (e) { toast.error("Failed"); }
    finally { setBusy(false); }
  };

  const handleClick = () => {
    if (editable) {
      setOpen(true);
    } else {
      navigate("/profile");
    }
  };

  return (
    <>
      <button
        data-testid="profile-photo-btn"
        onClick={handleClick}
        className={compact ? "w-9 h-9 rounded-full overflow-hidden bg-[#F3EFEA] flex items-center justify-center hover:ring-2 hover:ring-[#D4A373]" : "flex items-center gap-3 px-3 py-1.5 rounded-full border border-[#E5E0D8] hover:bg-[#F3EFEA]"}
        title={editable ? "Edit profile photo" : "Go to Profile"}
      >
        {src ? <img src={src} alt="" className="w-9 h-9 rounded-full object-cover" /> : <UserIcon className="w-4 h-4 text-[#5C6661]" />}
        {!compact && <span className="text-sm text-[#1C201E]">{user?.name || "Me"}</span>}
      </button>

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6" onClick={() => setOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl p-6 max-w-sm w-full">
            <h3 className="font-serif-display text-2xl text-[#1C201E]">Profile Photo</h3>
            <p className="text-xs text-[#5C6661] mt-1">JPG, PNG, WEBP or HEIC · Max 8 MB · Stored in church object storage.</p>
            <div className="mt-5 flex flex-col items-center">
              {src ? (
                <img src={src} alt="" className="w-28 h-28 rounded-full object-cover border border-[#E5E0D8]" />
              ) : (
                <div className="w-28 h-28 rounded-full bg-[#F3EFEA] flex items-center justify-center"><UserIcon className="w-10 h-10 text-[#5C6661]" /></div>
              )}
            </div>
            <div className="mt-6 flex flex-col gap-2">
              <button data-testid="profile-photo-upload" onClick={() => fileRef.current?.click()} disabled={busy} className="inline-flex items-center justify-center gap-2 px-5 py-2 rounded-full bg-[#2C4C3B] text-white text-sm hover:bg-[#1A3326] disabled:opacity-60">
                <Camera className="w-4 h-4" /> {busy ? "Uploading..." : (src ? "Replace photo" : "Upload photo")}
              </button>
              <input ref={fileRef} data-testid="profile-photo-file" type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
              {src && (
                <button data-testid="profile-photo-remove" onClick={remove} disabled={busy} className="inline-flex items-center justify-center gap-2 px-5 py-2 rounded-full border border-red-300 text-red-700 text-sm hover:bg-red-50 disabled:opacity-60">
                  <Trash2 className="w-4 h-4" /> Remove photo
                </button>
              )}
              <button onClick={() => setOpen(false)} className="px-5 py-2 rounded-full text-sm text-[#5C6661]">Close</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
